<template>
  <div class="scroller">
    <div class="scroller-wrapper w-full h-full flex transition-transform duration-500 ease-in-out" :style="{ transform: `translateX(-${currentIndex * 100}%)` }">
      <template v-if="Object.keys(displayPhotoBoxes).length > 0">
        <div v-for="([boxName, images], index) in Object.entries(displayPhotoBoxes)" :key="`${boxName}-${index}`" class="scroller-item">
          <div class="scroller-box">
            <div
              v-for="(content, contentIndex) in images"
              :key="`${boxName}-${contentIndex}`"
              class="scroller-image-div"
              :class="{
                'scroller-image-div-center': isCenterImage(images, contentIndex),
                'scroller-image-div-side': isSideImage(images, contentIndex),
              }"
            >
              <img
                class="cursor-pointer"
                :src="content.src"
                :style="getImageStyle(content.src)"
                :alt="content.title || `Photo of me in ${boxName}`"
                @click="handleImageClick(content.src, content.title || `Photo of me in ${boxName}`, $event)"
              >
            </div>
          </div>
        </div>
      </template>
      <template v-if="props.componentBoxes">
        <div v-for="(Component, compIndex) in props.componentBoxes" :key="compIndex" class="scroller-item">
          <div class="scroller-component-div">
            <component :is="Component" class="scroller-component-div-item"/>
          </div>
        </div>
      </template>
    </div>
    <div class="scroller-arrow-container">
      <CardArrow class="scroller-arrow-left" :direction="'left'" @scroll-left="prevItem"></CardArrow>
      <CardArrow class="scroller-arrow-right" :direction="'right'" @scroll-right="nextItem"></CardArrow>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, defineComponent, watch } from 'vue';
import { useImageViewer } from '../composables/useImageViewer';
import CardArrow from '../global/CardArrow.vue';

interface PhotoBoxImage {
  src: string;
  title?: string;
}

interface Props {
  photoBoxes?: Record<string, PhotoBoxImage[]>;
  componentBoxes?: Array<ReturnType<typeof defineComponent>>
}
const props = defineProps<Props>();

const { openImageMenu, imageViewIsOpen } = useImageViewer();

const currentIndex = ref<number>(0);
const imageSizes = ref<Record<string, { width: number; height: number }>>({});
const maxImageWidth = 360;
const maxImageHeight = 500;
const mobileBreakpoint = 1024;
const viewportWidth = ref(window.innerWidth);
const isMobileLayout = computed(() => viewportWidth.value < mobileBreakpoint);
const displayPhotoBoxes = computed<Record<string, PhotoBoxImage[]>>(() => {
  if (!props.photoBoxes) {
    return {};
  }

  if (!isMobileLayout.value) {
    return props.photoBoxes;
  }

  return Object.entries(props.photoBoxes).reduce<Record<string, PhotoBoxImage[]>>((accumulator, [boxName, images]) => {
    images.forEach((image, index) => {
      accumulator[`${boxName} ${index + 1}`] = [image];
    });
    return accumulator;
  }, {});
});
const totalItems = computed(() => {
  if (Object.keys(displayPhotoBoxes.value).length > 0) {
    return Object.keys(displayPhotoBoxes.value).length;
  } else if (props.componentBoxes) {
    return props.componentBoxes.length;
  }
  return 0;
});
let interval: ReturnType<typeof setInterval> | null = null;

const isCenterImage = (images: PhotoBoxImage[], index: number) => images.length > 1 && index === Math.floor(images.length / 2);

const isSideImage = (images: PhotoBoxImage[], index: number) => images.length > 1 && !isCenterImage(images, index);

const getScaledImageSize = (src: string) => {
  const naturalSize = imageSizes.value[src];
  const targetWidth = maxImageWidth;
  const targetHeight = maxImageHeight;

  if (!naturalSize) {
    return { width: targetWidth, height: targetHeight };
  }

  const width = naturalSize.width;
  const height = naturalSize.height;
  const scale = Math.min(targetWidth / width, targetHeight / height, 1);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const getImageStyle = (src: string) => {
  const size = getScaledImageSize(src);

  return {
    width: `${size.width}px`,
    height: `${size.height}px`,
    maxWidth: '100%',
    maxHeight: '100%',
    display: 'block',
    objectFit: 'contain' as const,
    borderRadius: '20px',
  } as const;
};

const handleImageClick = (src: string, title: string, event: MouseEvent) => {
  openImageMenu(src, title, event.currentTarget);
};

const loadImageDimensions = () => {
  const imageSources = props.photoBoxes ? Object.values(props.photoBoxes).flat().map((image) => image.src) : [];

  imageSources.forEach((src) => {
    if (imageSizes.value[src] || !src) {
      return;
    }

    const img = new Image();
    img.src = src;
    img.onload = () => {
      imageSizes.value[src] = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      };
    };
  });
};

watch(
  () => props.photoBoxes,
  () => {
    loadImageDimensions();
  },
  { deep: true }
);

watch(
  imageViewIsOpen,
  (isOpen) => {
    if (isOpen) {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
      return;
    }

    if (!interval && totalItems.value > 1) {
      interval = setInterval(() => {
        nextItemAuto();
      }, 8000);
    }
  },
  { immediate: true }
);

onMounted(() => {
  loadImageDimensions();
  viewportWidth.value = window.innerWidth;
  window.addEventListener('resize', loadImageDimensions);
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', loadImageDimensions);
  window.removeEventListener('resize', handleResize);
  if (interval) {
    clearInterval(interval);
  }
});

const handleResize = () => {
  viewportWidth.value = window.innerWidth;
};

const prevItem = (): void => {
  if (imageViewIsOpen.value) return;
  currentIndex.value = (currentIndex.value - 1 + totalItems.value) % totalItems.value; //makes sure that these are not neg
  if (interval) {
    clearInterval(interval);
    interval = setInterval(() => {
      nextItemAuto();
    }, 8000); // 8 seconds
  }
};

const nextItem = (): void => {
  if (imageViewIsOpen.value) return;
  currentIndex.value = (currentIndex.value + 1) % totalItems.value; //accounts for scrolling past last index
  if (interval) {
    clearInterval(interval);
    interval = setInterval(() => {
      nextItemAuto();
    }, 8000); // 8 seconds
  }
};

const nextItemAuto = (): void => {
  if (imageViewIsOpen.value) return;
  currentIndex.value = (currentIndex.value + 1) % totalItems.value; //accounts for scrolling past last index
};
</script>

<style scoped>
.scroller-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  transition: transform 0.9s ease-in-out;
  align-items: stretch;
}

.scroller-item {
  flex: 0 0 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 16px 16px;
  box-sizing: border-box;
  overflow: hidden;
}

.scroller-box {
  width: min(100%, 1200px);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  justify-items: center;
  align-items: center;
  gap: 24px;
  padding: 4px 0;
}

.scroller-image-div {
  width: 100%;
  max-width: 360px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: visible;
  isolation: isolate;
  border-radius: 20px;
  transition: transform 260ms ease, filter 260ms ease;
  transform-origin: center center;
}

.scroller-image-div-center {
  transform: scale(1.04);
  z-index: 1;
}

.scroller-image-div-side {
  transform: scale(0.98);
  filter: saturate(0.96);
}

.scroller-image-div img {
  display: block;
  object-fit: contain;
  border-radius: 20px;
  box-shadow: 0 6px 14px rgba(144, 183, 167, 0.4);
}

.scroller-component-div {
  margin: 16px;
  overflow: hidden;
}

.scroller-component-div-item {
  width: auto;
  height: auto;
}

.scroller {
  position: relative;
}

.scroller-arrow-container {
  position: relative;
  display: flex;
  margin-left: 60px;
  margin-right: 60px;
  justify-content: space-between;
  align-items: center;
  z-index: 2;
}

.scroller-arrow-left,
.scroller-arrow-right {
  position: relative;
  cursor: pointer;
  z-index: 2;
}

.scroller-arrow-left {
  transform: rotate(-90deg);
}

.scroller-arrow-right {
  transform: rotate(90deg);
}
</style>
